import axios from "axios";

// Selectors
import { selectAPIUrl, selectAPIToken } from "../selectors/app";
import {
  selectJourneyPassengers,
  selectPickedLegSolutions,
  selectPickedPrices,
} from "../selectors/journeys";
import {
  SET_BOOKING,
  SET_ERROR_CREATING_BOOKING,
  SET_IS_CREATING_BOOKING,
} from "./constants";

// Actions

export const setIsCreatingBooking = (isCreating) => (dispatch) =>
  dispatch({
    type: SET_IS_CREATING_BOOKING,
    isCreating,
  });

export const setBooking = (booking) => (dispatch) =>
  dispatch({
    type: SET_BOOKING,
    booking,
  });

export const setErrorCreatingBooking = (error) => (dispatch) =>
  dispatch({
    type: SET_ERROR_CREATING_BOOKING,
    error,
  });

// Thunks
export const createBooking = () => (dispatch, getState) => {
  dispatch(setBooking(null));
  dispatch(setErrorCreatingBooking(false));
  const token = selectAPIToken(getState());
  const url = selectAPIUrl(getState());

  // Format passenger information for booking creation
  // maybe the selector could do this?
  const passengers = selectJourneyPassengers(getState()).map((passenger) => ({
    passengerID: passenger.attributes.passengerSpecID,
    ageAtTimeOfTravel: passenger.age,
  }));

  const legSolutions = selectPickedLegSolutions(getState());

  // Dirty fixing datetimes to be in the format the API expects
  const prices = selectPickedPrices(getState()).map((price) => ({
    ...price,
    ticketableFares: {
      ticketableFare: price.ticketableFares.ticketableFare.map((fare) => ({
        ...fare,
        outwardFareExpirationDate: fare.outwardFareExpirationDate.replace(
          "T00:00:00.000Z",
          ""
        ),
        returnFareExpirationDate: fare.returnFareExpirationDate?.replace(
          "T00:00:00.000Z",
          ""
        ),
      })),
    },
  }));

  dispatch(setIsCreatingBooking(true));
  axios
    .post(
      `${url}/v1/bookings`,
      {
        passengers,
        legSolutions,
        prices,
      },
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) => {
      dispatch(retrieveBooking(data.recordLocator));
    })
    .catch((data) => {
      dispatch(setErrorCreatingBooking(true));
      dispatch(setIsCreatingBooking(false));
    });
};

export const retrieveBooking = (recordLocator) => (dispatch, getState) => {
  const token = selectAPIToken(getState());
  const url = selectAPIUrl(getState());

  axios
    .get(`${url}/v1/bookings/${recordLocator}`, {
      headers: {
        Authorization: token,
      },
    })
    .then(({ data }) => {
      dispatch(setBooking(data));
      dispatch(setIsCreatingBooking(false));
    });
};

export const doQuickBooking = () => (dispatch, getState) => {
  const token = selectAPIToken(getState());
  const url = selectAPIUrl(getState());

  const { origin, destination, isReturn } = getState().router.location.query;

  axios
    .get(`${url}/v1/journeys`, {
      params: {
        origin: origin,
        destination: destination,
        return: isReturn,
        adults: 1,
        // Suggested by Ben Fenn in SD-24879
        fareFilter: "CHEAPEST_ONEWAY_AND_ROUNDTRIP",
      },
      headers: {
        Authorization: token,
      },
    })
    .then(({ data }) => {
      // We got journeys, let's pick the first Off Peak ticket
      const {
        legs,
        fareInformation: { prices },
      } = data;
      const returnJourney = isReturn;

      const filterOutFirstClass = (price) =>
        price.ticketableFares.ticketableFare[0].passengerReferences
          .passengerReference[0].fareCodes.fareCode[0].cabinClass ===
        "Standard";
      prices.pointToPointPrice =
        prices.pointToPointPrice.filter(filterOutFirstClass);

      const outboundLegSolutionIds =
        returnJourney === "true"
          ? prices.pointToPointPrice
              .filter(
                (price) => price.restrictiveFareClass === "OFFPEAK_DAY_RETURN"
              )
              .flatMap((price) => price.legReferences.legSolutionIDRef)
          : prices.pointToPointPrice
              .filter((price) => price.restrictiveFareClass === "OFFPEAK_DAY")
              .flatMap((price) => price.legReferences.legSolutionIDRef);

      const returnLegSolutionIds =
        returnJourney === "true"
          ? prices.pointToPointPrice
              .filter(
                (price) => price.restrictiveFareClass === "OFFPEAK_DAY_RETURN"
              )
              .flatMap((price) => price.legReferences.legSolutionIDRef)
          : [];

      const sortByPrice = (p1, p2) =>
        p1.totalPrice.attributes.$value - p2.totalPrice.attributes.$value || [];

      const outboundPrices =
        returnJourney === "true"
          ? prices.pointToPointPrice
              .filter(
                (price) => price.restrictiveFareClass === "OFFPEAK_DAY_RETURN"
              )
              .sort(sortByPrice)
          : prices.pointToPointPrice
              .filter((price) => price.restrictiveFareClass === "OFFPEAK_DAY")
              .sort(sortByPrice);

      const outboundLegSolutions = legs.leg[0].legSolutions.legSolution;
      const returnLegSolutions =
        returnJourney === "true" ? legs.leg[1].legSolutions.legSolution : [];

      const sortByTime = (l1, l2) =>
        new Date(l1.travelSegments.travelSegment[0].departureDateTime) -
          new Date(l2.travelSegments.travelSegment[0].departureDateTime) || [];

      const selectedOutboundLegSolution = outboundLegSolutions
        .filter((legSolution) =>
          outboundLegSolutionIds.includes(legSolution.attributes.legSolutionID)
        )
        .sort(sortByTime)[0];
      const selectedReturnLegSolution =
        returnJourney === "true"
          ? [
              returnLegSolutions
                .filter((legSolution) =>
                  returnLegSolutionIds.includes(
                    legSolution.attributes.legSolutionID
                  )
                )
                .sort(sortByTime)[0],
            ]
          : [];
      const selectedlegSolutionIDS =
        returnJourney === "true"
          ? [
              selectedOutboundLegSolution.attributes.legSolutionID,
              selectedReturnLegSolution[0].attributes.legSolutionID,
            ]
          : [selectedOutboundLegSolution.attributes.legSolutionID];
      const selectedOutboundPrice = outboundPrices.find(
        (price) =>
          [price.legReferences.legSolutionIDRef].flat().toString() ===
          selectedlegSolutionIDS.toString()
      );

      const booking = {
        passengers: [
          {
            passengerID: "PAX_SPEC_0",
            ageAtTimeOfTravel: 20,
          },
        ],
        legSolutions: [
          selectedOutboundLegSolution,
          ...selectedReturnLegSolution,
        ],
        prices: [selectedOutboundPrice],
      };

      axios
        .post(
          `${url}/v1/bookings`,
          {
            ...booking,
          },
          {
            headers: {
              Authorization: token,
            },
          }
        )
        .then(({ data }) => {
          window.location = data.url;
        });
    });
};
